/**
 * §13.4 — İşlem kapasitesi ve süre tahmini.
 *
 * *"UI'da işlem başlamadan tahmini süre ve token maliyeti gösterilecek.
 * Kullanıcı onaylamadan başlama."*
 *
 * Bu kural 100.000 ASIN'lik bir aramanın günler sürebilmesinden geliyor.
 * Kullanıcı "başlat"a bastıktan sonra öğrenmemeli.
 */

import { jetonMaliyeti } from '../keepa/token-budget.js';

/** §13.4 — Şartnamedeki ölçülmüş hızlar. */
export const RATES = {
  /** Keşif isteği `buybox` istiyor (keşif hattı); ASIN başına jeton ölçülmüş (`jetonMaliyeti`). */
  keepaTokensPerAsin: jetonMaliyeti({ buybox: true }),
  /** Restrictions: ASIN başına 1 istek, 5 istek/sn. */
  restrictionsRequestsPerSecond: 5,
} as const;

export interface DiscoveryEstimate {
  asinCount: number;
  /** Filtrelerden geçip pahalı adımlara ulaşacağı tahmin edilen ürün sayısı. */
  expectedSurvivors: number;
  keepaTokens: number;
  /** Adım adım süre (saniye). */
  breakdown: Array<{ step: string; label: string; seconds: number }>;
  totalSeconds: number;
  /** Kullanıcıya gösterilecek Türkçe özet. */
  summary: string;
  /** Keepa bütçesi yetiyor mu? */
  keepaBudgetEnough: boolean;
  keepaWarning: string | null;
}

/** Saniyeyi Türkçe okunur süreye çevirir. */
export function humanizeDuration(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)} saniye`;
  if (seconds < 3_600) return `${Math.ceil(seconds / 60)} dakika`;
  if (seconds < 86_400) {
    const hours = seconds / 3_600;
    return `${hours < 10 ? hours.toFixed(1).replace('.', ',') : Math.round(hours)} saat`;
  }
  const days = seconds / 86_400;
  return `${days < 10 ? days.toFixed(1).replace('.', ',') : Math.round(days)} gün`;
}

/**
 * §13.4 — Arama maliyetini ve süresini tahmin eder.
 *
 * `survivalRate`, filtrelerden geçme oranı tahmini. Pahalı adım (Restrictions)
 * yalnızca hayatta kalanlara uygulandığı için bu oran süreyi etkiliyor. Gerçekçi bir varsayılan kullanılıyor
 * ve tahmin olduğu ekranda söyleniyor — kesin süre vaadi vermek, saatlerce
 * bekleyen kullanıcıyı yanıltmak olurdu.
 */
export function estimateDiscovery(
  asinCount: number,
  options: {
    survivalRate?: number;
    keepaDailyBudgetRemaining?: number | null;
    keepaTokensPerMinute?: number;
  } = {},
): DiscoveryEstimate {
  const survivalRate = Math.min(1, Math.max(0, options.survivalRate ?? 0.1));
  const survivors = Math.ceil(asinCount * survivalRate);

  const keepaTokens = asinCount * RATES.keepaTokensPerAsin;
  const keepaSeconds =
    options.keepaTokensPerMinute && options.keepaTokensPerMinute > 0
      ? (keepaTokens / options.keepaTokensPerMinute) * 60
      : 0;

  const restrictionsSeconds = survivors / RATES.restrictionsRequestsPerSecond;

  const breakdown = [
    { step: 'KEEPA', label: 'amazon.com verisi (Keepa)', seconds: keepaSeconds },
    { step: 'RESTRICTIONS', label: 'Listeleme izni kontrolü', seconds: restrictionsSeconds },
  ];

  const totalSeconds = breakdown.reduce((sum, b) => sum + b.seconds, 0);

  const remaining = options.keepaDailyBudgetRemaining;
  const keepaBudgetEnough = remaining === null || remaining === undefined || remaining >= keepaTokens;

  return {
    asinCount,
    expectedSurvivors: survivors,
    keepaTokens,
    breakdown,
    totalSeconds,
    keepaBudgetEnough,
    keepaWarning: keepaBudgetEnough
      ? null
      : `Bu arama ${keepaTokens.toLocaleString('tr-TR')} Keepa token istiyor ama ürün aramasının ` +
        `bugünkü payından ${(remaining ?? 0).toLocaleString('tr-TR')} token kaldı. Arama gün ` +
        'dönümünde kaldığı yerden devam eder.',
    summary:
      `${asinCount.toLocaleString('tr-TR')} ASIN · yaklaşık ${humanizeDuration(totalSeconds)} · ` +
      `${keepaTokens.toLocaleString('tr-TR')} Keepa token. ` +
      `Bu bir tahmin: gerçek süre kaç ürünün filtrelerden geçtiğine göre değişir.`,
  };
}

/**
 * Manuel (Keepa'sız) arama tahmini (21 Eki 2026): amazon.com sayfası sırayla, sayfa başına 3-5 sn
 * bekleme + indirme; "See All Buying Options" gereken üründe ikinci sayfa. Ürün başına ~6 sn.
 */
export const MANUEL_SANIYE_PER_ASIN = 6;

export function manuelTahmin(tahmin: DiscoveryEstimate): DiscoveryEstimate {
  const breakdown = tahmin.breakdown.map((b) =>
    b.step === 'KEEPA'
      ? {
          step: 'MANUEL',
          label: 'amazon.com sayfası (manuel, sayfa başına 3-5 sn)',
          seconds: tahmin.asinCount * MANUEL_SANIYE_PER_ASIN,
        }
      : b,
  );
  const totalSeconds = breakdown.reduce((sum, b) => sum + b.seconds, 0);
  return {
    ...tahmin,
    keepaTokens: 0,
    breakdown,
    totalSeconds,
    keepaBudgetEnough: true,
    keepaWarning: null,
    summary:
      `${tahmin.asinCount.toLocaleString('tr-TR')} ASIN · yaklaşık ${humanizeDuration(totalSeconds)} · ` +
      'Keepa kullanılmaz (ücretsiz). Ürünler amazon.com’da sırayla okunur; bu bir tahmin.',
  };
}

